import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { TenantBranding } from '@ck/sdk';
import logoOnLight from './assets/logo-on-light.png';
import logoOnDark from './assets/logo-on-dark.png';
import markOnLight from './assets/mark-on-light.png';
import markOnDark from './assets/mark-on-dark.png';

export { logoOnLight, logoOnDark, markOnLight, markOnDark };

/** The firm's logo. CK uses its own artwork; a white-label firm uses its uploaded logo or its letter mark. */
export function Logo({ branding, on = 'light', compact = false, height = 30 }: { branding: TenantBranding; on?: 'light' | 'dark'; compact?: boolean; height?: number }) {
  if (branding.logoUrl) return <img className="logo" src={branding.logoUrl} alt={branding.name} height={height} />;
  if (/^CK\b/i.test(branding.name)) {
    const src = compact ? (on === 'dark' ? markOnDark : markOnLight) : on === 'dark' ? logoOnDark : logoOnLight;
    return <img className="logo" src={src} alt={branding.name} height={height} />;
  }
  return <span className="logo-letters" style={{ height, width: height, fontSize: height * 0.42 }} aria-label={branding.name}>{branding.mark}</span>;
}

/** Sets the CK favicon unless the firm has its own logo. */
export function useFavicon(branding: TenantBranding | undefined) {
  useEffect(() => {
    if (!branding) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = branding.logoUrl ?? markOnLight;
  }, [branding]);
}

const reducedMotion = () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The sign-in backdrop: the stripes of the CK mark sweeping behind a live-drawn candle chart.
 * Drawn in code, so it is light, sharp on every screen and never needs a licensed video.
 */
export function MarketCanvas({ accent = '#FEAB41', ink = '#F1E6CC' }: { accent?: string; ink?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current!;
    const ctx = canvas.getContext('2d')!;
    let w = 0, h = 0, raf = 0, last = performance.now();
    const still = reducedMotion();
    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      w = canvas.clientWidth; h = canvas.clientHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    // A random walk with momentum, so the chart looks like a market rather than noise.
    const STEP = 14;
    let price = 100, drift = 0, seed = 7;
    const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
    const candle = () => {
      drift = drift * 0.86 + (rnd() - 0.5) * 0.9 - (price - 100) * 0.006;
      const open = price;
      const close = open + drift + (rnd() - 0.5) * 1.4;
      const high = Math.max(open, close) + rnd() * 1.3;
      const low = Math.min(open, close) - rnd() * 1.3;
      price = close;
      return { open, close, high, low };
    };
    const candles = Array.from({ length: 220 }, candle);
    let offset = 0;

    const draw = (dt: number) => {
      ctx.clearRect(0, 0, w, h);
      // Stripes from the K mark, drifting slowly.
      const t = performance.now() / 1000;
      ctx.save();
      ctx.translate(w * 0.62, h * 0.5);
      ctx.rotate(-Math.PI / 4);
      for (let i = -6; i <= 6; i++) {
        const x = i * 150 + ((still ? 0 : t * 6) % 150);
        ctx.fillStyle = i % 3 === 0 ? 'rgba(254,171,65,0.05)' : 'rgba(241,230,204,0.035)';
        ctx.fillRect(x, -h * 2, 88, h * 4);
      }
      ctx.restore();

      // Grid.
      ctx.strokeStyle = 'rgba(241,230,204,0.06)';
      ctx.lineWidth = 1;
      for (let y = h * 0.18; y < h; y += h * 0.16) { ctx.beginPath(); ctx.moveTo(0, Math.round(y) + 0.5); ctx.lineTo(w, Math.round(y) + 0.5); ctx.stroke(); }

      if (!still) {
        offset += dt * 0.012;
        while (offset >= STEP) { offset -= STEP; candles.shift(); candles.push(candle()); }
      }
      const visible = Math.ceil(w / STEP) + 2;
      const view = candles.slice(-visible);
      const lo = Math.min(...view.map((c) => c.low)), hi = Math.max(...view.map((c) => c.high));
      const top = h * 0.2, bottom = h * 0.82;
      const y = (v: number) => bottom - ((v - lo) / (hi - lo || 1)) * (bottom - top);
      view.forEach((c, i) => {
        const x = i * STEP - offset;
        const up = c.close >= c.open;
        const fade = Math.min(1, (i + 1) / 18);
        ctx.globalAlpha = 0.85 * fade;
        ctx.strokeStyle = up ? accent : ink;
        ctx.fillStyle = up ? accent : 'rgba(0,0,0,1)';
        ctx.beginPath(); ctx.moveTo(x + 4.5, y(c.high)); ctx.lineTo(x + 4.5, y(c.low)); ctx.stroke();
        const yo = y(c.open), yc = y(c.close);
        const bodyTop = Math.min(yo, yc), bodyH = Math.max(1.5, Math.abs(yc - yo));
        ctx.fillRect(x + 1, bodyTop, 7, bodyH);
        if (!up) ctx.strokeRect(x + 1.5, bodyTop + 0.5, 6, bodyH - 1);
      });
      ctx.globalAlpha = 1;
      // Last price line.
      const lastC = view[view.length - 1]!;
      const ly = Math.round(y(lastC.close)) + 0.5;
      ctx.setLineDash([3, 4]);
      ctx.strokeStyle = 'rgba(254,171,65,0.55)';
      ctx.beginPath(); ctx.moveTo(0, ly); ctx.lineTo(w, ly); ctx.stroke();
      ctx.setLineDash([]);
    };
    const loop = (now: number) => { draw(Math.min(64, now - last)); last = now; raf = requestAnimationFrame(loop); };
    if (still) draw(0); else raf = requestAnimationFrame(loop);
    const onVis = () => { if (document.hidden) cancelAnimationFrame(raf); else if (!still) { last = performance.now(); raf = requestAnimationFrame(loop); } };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); document.removeEventListener('visibilitychange', onVis); };
  }, [accent, ink]);
  return <canvas ref={ref} className="market-canvas" aria-hidden="true" />;
}

/** Full-screen sign-in and sign-up layout shared by the CRM, client portal and partner portal. */
export function AuthStage({ branding, product, headline, sub, points, videoUrl, children }: {
  branding: TenantBranding; product: string; headline: ReactNode; sub?: ReactNode; points?: string[]; videoUrl?: string | null; children: ReactNode;
}) {
  const [videoOk, setVideoOk] = useState(!!videoUrl && !reducedMotion());
  return (
    <div className="stage">
      <div className="stage-bg" aria-hidden="true">
        {videoOk && videoUrl
          ? <video className="stage-video" src={videoUrl} autoPlay muted loop playsInline onError={() => setVideoOk(false)} />
          : <MarketCanvas accent={branding.primary} />}
        <div className="stage-shade" />
      </div>
      <header className="stage-top">
        <Logo branding={branding} on="dark" height={34} />
        <span className="stage-product">{product}</span>
      </header>
      <section className="stage-copy">
        <h1>{headline}</h1>
        {sub && <p>{sub}</p>}
        {points && <ul>{points.map((x) => <li key={x}>{x}</li>)}</ul>}
      </section>
      <main className="stage-card">{children}</main>
      <footer className="stage-foot">© {new Date().getFullYear()} {branding.name}</footer>
    </div>
  );
}

/** Password field with a show/hide toggle. */
export function PasswordInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const [show, setShow] = useState(false);
  return (
    <span className="pw">
      <input {...props} type={show ? 'text' : 'password'} />
      <button type="button" className="pw-t" onClick={() => setShow(!show)} aria-label={show ? 'Hide password' : 'Show password'}>{show ? 'Hide' : 'Show'}</button>
    </span>
  );
}
export * from './certificate';
export * from './chat';
export * from './call';
export * from './charts';
export * from './sound';
export * from './motion';
export * from './avatar';
export * from './update';
export * from './theme';
export * from './popup';
export * from './certbrowser';
export * from './flag';
export * from './flagicon';
export * from './transparency';
export * from './pills';

export { AccountDash } from './accountdash';
export { NotebookUI, type NoteApi, type NoteRow as NotebookRow } from './notebook';
export { applyAutoTranslate } from './autotranslate';
